import { Box, Flex, useBreakpointValue } from "@chakra-ui/react";
import { Heading } from "components/Heading";
import LogoWrapper from "components/LogoWrapper";
import { useWhenWindowHeightChanges } from "newui/hooks/useWhenWindowHeightChanges";
import React, { ReactNode } from "react";
import rightImg from "./../../assets/login-right-img.webp";

/**
 * Props for LoginInviteTokenSelectAccountPath
 * Extend this interface as requirements become clearer.
 */
export interface AuthContainerProps {
	additionalHeadingText: | ReactNode;
	children: React.ReactNode;
	logoSizeOverride?: "sm";
	welcomeOverrideText?: string;
}

/**
 * LoginInviteTokenSelectAccountPath
 *
 * Handles the flow where a user logs in via an invite token
 * and must select which account/path to continue with.
 */
export const AuthContainer: React.FC<
	AuthContainerProps
> = ({ additionalHeadingText, children, logoSizeOverride, welcomeOverrideText }) => {

	const imgBackgroundPosition = useBreakpointValue({
		base: "0% 70%", // mobile (not shown anyway)
		sm: "50% 90%", // small tablets
		md: "60% 90%", // tablet / desktop
		l: "2% 80%", // tablet / desktop
		xl: "0% 70%", // extra large screens
	});



	const getMarginBottom = (height: number) => {

		if (height < 720) {
			return "2vh";
		}
		if (height <= 740) {
			return "5vh";
		}
		if (height < 830) {
			return "10vh";
		}
		return "100px";
	};

	const logoHeight = useWhenWindowHeightChanges<number>((height) => {
		const width = window.innerWidth;

		if (width <= 325) {
			return 48;
		}

		if (width <= 344) {
			return 50;
		}

		if (height < 600) {
			return 40;
		}

		if (height <= 870) {
			return 50;
		}

		return 60;
	});

	const marginBottom = useWhenWindowHeightChanges<string>(getMarginBottom);
	const fontSize = useWhenWindowHeightChanges<string>((height) => {
		if (height < 720) {
			return "30px";
		}
		return "3rem";
	});

	return (
		<Flex
			className="min-h-screen w-screen flex-col bg-white md:flex-row"
			fontFamily="Roboto, sans-serif"
			style={{ minWidth: "320px" }}
		>
			{/* Left Section */}
			<Flex
				direction="column"
				className="w-full shadow-lg md:w-3/5 md:max-w-4xl md:p-10"
				zIndex={1}
				justify="center" // vertical center
				align="center" // horizontal center
				px={{ base: 5, md: 10 }}
				py={{ base: 5, md: 10 }}
			>
				<Box className="w-full max-w-md">
					{/* constrain content width */}
					<LogoWrapper
						marginBottom={logoSizeOverride ? "10px" : marginBottom}
						heightPixels={logoSizeOverride ? 30 : logoHeight}
					/>

					{(welcomeOverrideText || additionalHeadingText) && <Heading
						style={{ fontSize: fontSize }}
						className="pb-10 text-left font-bold leading-tight text-gray-800"
					>
						<span className="mb-4 block">{welcomeOverrideText ? welcomeOverrideText : 'Welcome to Procuracon'}</span>
						<span className="text-[0.75em]">{additionalHeadingText}</span>
					</Heading>}
					{children}
				</Box>
			</Flex>

			{/* Right Section (hidden on mobile) */}
			<Box
				className="hidden flex-1 md:block"
				display={{ base: "none", md: "block" }}
				style={{
					backgroundImage: `url(${rightImg})`,
					backgroundSize: "cover",
					backgroundPosition: imgBackgroundPosition,
					backgroundRepeat: "no-repeat",
					minHeight: "100vh",
				}}
			/>
		</Flex>
	);
};
